import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  X,
  Eye,
  Calculator,
  Loader2,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { propostaService, Proposta } from "../../services/propostaService";

const statusConfig = {
  rascunho: { label: "Rascunho", color: "bg-gray-500/20 text-gray-400", icon: Clock },
  enviada: { label: "Enviada", color: "bg-blue-500/20 text-blue-400", icon: Clock },
  aprovada: { label: "Aprovada", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  rejeitada: { label: "Rejeitada", color: "bg-red-500/20 text-red-400", icon: XCircle },
};

export function Propostas() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/area-cliente");
    }
  }, [isAuthenticated, navigate]);

  // Para clientes, usa clientId se disponível, senão usa id (login legado)
  const clientId = user?.clientId || (user?.role === 'client' ? user?.id : undefined);

  useEffect(() => {
    if (clientId) {
      loadPropostas();
    }
  }, [clientId, statusFilter, currentPage]);

  const loadPropostas = async () => {
    if (!clientId) return;
    
    setIsLoading(true);
    try {
      console.log("Buscando propostas para clientId:", clientId);
      const result = await propostaService.list({
        clientId: clientId,
        status: statusFilter || undefined,
        page: currentPage,
        limit: 20,
      });
      console.log("Propostas recebidas:", result.data);
      // Filtrar apenas propostas visíveis no portal (não rascunhos)
      // Verifica tanto boolean true quanto number 1 (MySQL retorna 0/1)
      const propostasVisiveis = result.data.filter((p: Proposta) => p.visibleInPortal === true || (p.visibleInPortal as any) === 1);
      console.log("Propostas visíveis:", propostasVisiveis);
      setPropostas(propostasVisiveis);
      setTotalPages(Math.ceil(propostasVisiveis.length / 20) || 1);
    } catch (error) {
      console.error("Erro ao carregar propostas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
  };

  const formatNumero = (numero: number, dataEmissao: string) => {
    const num = String(numero).padStart(3, "0");
    const data = new Date(dataEmissao);
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const ano = data.getFullYear();
    return `${num}-${mes}-${ano}`;
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const filteredPropostas = propostas.filter((p) =>
    formatNumero(p.numero, p.dataEmissao).includes(searchTerm) ||
    p.tipo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Propostas | Portal Arcgel</title>
      </Helmet>

      <div className="min-h-screen pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Portal
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              <span className="bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent">
                Propostas Comerciais
              </span>
            </h1>
          </motion.div>

          {/* Filter Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col md:flex-row gap-4 mb-6 p-4 rounded-xl bg-slate-800/30 border border-slate-700/50"
          >
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar propostas..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-slate-900/50 border border-slate-600 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-600 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
              >
                <option value="">Todos os status</option>
                <option value="enviada">Enviada</option>
                <option value="aprovada">Aprovada</option>
                <option value="rejeitada">Rejeitada</option>
              </select>
              {(searchTerm || statusFilter) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Limpar
                </button>
              )}
            </div>
          </motion.div>

          {/* Propostas Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl bg-slate-800/30 border border-slate-700/50 overflow-hidden"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : filteredPropostas.length === 0 ? (
              <div className="text-center py-12">
                <Calculator className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Nenhuma proposta encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Proposta</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden md:table-cell">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Valor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden md:table-cell">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden lg:table-cell">Emissão</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase hidden lg:table-cell">Validade</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {filteredPropostas.map((proposta) => {
                      const status = statusConfig[proposta.status];
                      const StatusIcon = status.icon;
                      return (
                        <tr key={proposta.id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <Calculator className="w-5 h-5 text-blue-400 flex-shrink-0" />
                              <span className="text-white font-medium">
                                #{formatNumero(proposta.numero, proposta.dataEmissao)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 hidden md:table-cell">
                            <span className="text-slate-300 text-sm capitalize">{proposta.tipo}</span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-green-400 font-semibold">
                              {formatCurrency(Number(proposta.valorTotal))}
                            </span>
                          </td>
                          <td className="px-4 py-4 hidden md:table-cell">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-4 hidden lg:table-cell">
                            <span className="inline-flex items-center gap-1 text-slate-400 text-sm">
                              <Calendar className="w-3 h-3" />
                              {new Date(proposta.dataEmissao).toLocaleDateString("pt-BR")}
                            </span>
                          </td>
                          <td className="px-4 py-4 hidden lg:table-cell">
                            <span className="text-slate-400 text-sm">
                              {new Date(proposta.dataValidade).toLocaleDateString("pt-BR")}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Link
                                to={`/portal/propostas/${proposta.id}`}
                                className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                                title="Visualizar"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-700/50">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <span className="text-slate-400 text-sm">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Próxima
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
